import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Select from 'react-select'; // ✅ added
import {
  SALES_ORDER_SEARCH_API,
  SALES_ORDER_CUSTOMER_LIST,
  MATERIAL_LIST_ENDPOINT
} from '../../config/api';
import { decryptData } from '../../utils/encrypt';
import PlanningTable from './search/PlanningTable';

function SalesOrderPlanningSearch() {
  const location = useLocation();
  const isSetVehicle = location.pathname.includes('setvehicle');

  const [filters, setFilters] = useState({});
  const [orders, setOrders] = useState([]);
  const [, setSelectedOrder] = useState(null);
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [customerList, setCustomerList] = useState([]);
  const [materialList, setMaterialList] = useState([]);

  useEffect(() => {
    const storedUser = localStorage.getItem('suyog_user');
    const storedOrg = localStorage.getItem('suyog_selected_org');

    if (storedUser) {
      const parsed = decryptData(storedUser);
      const uid = parsed.user_id || null;
      setUserId(uid);

      if (uid) {
        fetch(SALES_ORDER_CUSTOMER_LIST, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: uid })
        })
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data)) {
              setCustomerList(data);
            } else if (data.success && Array.isArray(data.data)) {
              setCustomerList(data.data);
            } else {
              console.error("Failed to load customers:", data.error);
            }
          })
          .catch(err => console.error("Customer list error:", err));
      }
    }

    if (storedOrg) {
      const parsedOrg = JSON.parse(storedOrg);
      setOrgId(parsedOrg.value || null);
    }

    // Set default date filters
    const today = new Date();
    const toDate = new Date(today);
    toDate.setDate(toDate.getDate() + 10);
    const fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const format = (d) => d.toISOString().split('T')[0];

    setFilters({
      po_date_from: format(fromDate),
      po_date_to: format(toDate),
      customer_id: '',
      customer_po_no: '',
      material_id: '',
      packing_type: '',
      dispatch_status: ''
    });

    // Load Material List
    fetch(MATERIAL_LIST_ENDPOINT)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setMaterialList(data);
        } else {
          console.error('Unexpected material list response:', data);
        }
      })
      .catch(err => console.error('Failed to load material list', err));

    setLoadingUser(false);
  }, []);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!userId || !orgId) {
      alert("User or Organization not loaded.");
      return;
    }

    try {
      const res = await fetch(SALES_ORDER_SEARCH_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...filters, user_id: userId, org_id: orgId })
      });
      const result = await res.json();
      if (res.ok) {
        setOrders(result.data || []);
      } else {
        alert(result.error || 'Search failed');
      }
    } catch (err) {
      alert('Server error');
    }
  };

  const handleReset = () => {
    const today = new Date();
    const toDate = new Date(today);
    toDate.setDate(toDate.getDate() + 10);
    const fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const format = (d) => d.toISOString().split('T')[0];

    setFilters({
      po_date_from: format(fromDate),
      po_date_to: format(toDate),
      customer_id: '',
      customer_po_no: '',
      material_id: '',
      packing_type: '',
      dispatch_status: ''
    });

    setOrders([]);
  };

  // ✅ react-select options for Customer (type to search)
  const customerOptions = useMemo(
    () =>
      (customerList || []).map(c => ({
        value: String(c.id),
        label: c.account_name
      })),
    [customerList]
  );

  const selectedCustomerOption = useMemo(
    () =>
      customerOptions.find(o => String(o.value) === String(filters.customer_id || '')) || null,
    [customerOptions, filters.customer_id]
  );

  return (
    <>
      <Navbar />
      <div className="container mt-3">
        <div className="card shadow-sm rounded-4 border">
          <div className="card-body">
            <h4 className="card-title mb-4 text-primary">Search & Vehicle Planning : Sales Order Planning</h4>
            The Search will help you to find the Sales Orders which are planned for Dispatch. This is bind with <span style={{ color: 'red', fontWeight: 'bold' }}>Delivery Date</span>.

            <form onSubmit={handleSearch} className="mb-3">
              <div className="row g-3">
                {/* 🔎 Customer - react-select (typeable) */}
                <div className="col-md-3">
                  <Select
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={(opt) =>
                      setFilters(f => ({
                        ...f,
                        customer_id: opt ? String(opt.value) : ''
                      }))
                    }
                    isClearable
                    isSearchable
                    placeholder="-- All Customers --"
                    classNamePrefix="react-select"
                  />
                </div>

                <div className="col-md-2">
                  <input
                    className="form-control"
                    name="customer_po_no"
                    placeholder="PO No"
                    value={filters.customer_po_no || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-2">
                  <input
                    type="date"
                    className="form-control"
                    name="po_date_from"
                    value={filters.po_date_from || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-2">
                  <input
                    type="date"
                    className="form-control"
                    name="po_date_to"
                    value={filters.po_date_to || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select"
                    name="material_id"
                    value={filters.material_id || ''}
                    onChange={handleChange}
                  >
                    <option value="">-- All Materials --</option>
                    {materialList.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.material_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <select
                    className="form-select"
                    name="packing_type"
                    value={filters.packing_type || ''}
                    onChange={handleChange}
                  >
                    <option value="">-- All Packing --</option>
                    <option value="25 Kgs Bags">25 Kgs Bags</option>
                    <option value="50 Kgs Bag">50 Kgs Bag</option>
                    <option value="Loose Bulker">Loose Bulker</option>
                  </select>
                </div>

                <div hidden className="col-md-2">
                  <select
                    className="form-select"
                    name="dispatch_status"
                    value={filters.dispatch_status || ''}
                    onChange={handleChange}
                  >
                    <option value="">-- Vehicle : All --</option>
                    <option value="done">Done</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>

              <div className="mt-3">
                <button className="btn btn-primary btn-sm me-2" type="submit" disabled={loadingUser}>
                  {loadingUser ? 'Loading...' : 'Search'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleReset}>
                  Reset
                </button>
              </div>
            </form>

            {orders.length > 0 && (
              <PlanningTable
                orders={orders}
                onSelectOrder={setSelectedOrder}
                onDispatchCreate={() => {}}
                showDispatchButton={isSetVehicle}
                setOrders={setOrders}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default SalesOrderPlanningSearch;
